import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import TifpaLogo from '@/components/ui/TifpaLogo';
import LegalContent from '@/components/layout/LegalContent';
import { LEGAL_DOCS, LegalDocKey } from '@/lib/legal';

/**
 * Full-screen page for a legal document (/privacy-policy,
 * /terms-of-service). Uses its own minimal header/footer instead of
 * Navbar/Footer — those smooth-scroll to section ids that only exist on
 * the home page.
 */
const LegalPage = ({ doc }: { doc: LegalDocKey }) => {
  const { title, content } = LEGAL_DOCS[doc];
  const otherKey: LegalDocKey = doc === 'privacy' ? 'terms' : 'privacy';
  const other = LEGAL_DOCS[otherKey];
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const previousTitle = document.title;
    document.title = `${title} | TIFPA Consulting LLC`;
    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <header className="sticky top-0 z-40 border-b border-stone-200 bg-white/80 backdrop-blur-sm">
        <div className="container flex items-center justify-between py-3">
          <Link to="/" aria-label="TIFPA Consulting home">
            <TifpaLogo />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-sm font-medium text-stone-700 transition-colors hover:text-tifpa-rose"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <div className="container section">
          <div className="mx-auto max-w-3xl">
            <h1 className="font-serif text-3xl text-stone-800 md:text-4xl">
              {title}
            </h1>
            <div className="mb-8 mt-3 h-1 w-16 rounded-full bg-tifpa-rose" />
            <LegalContent markdown={content} />
          </div>
        </div>
      </main>

      <footer className="bg-stone-800 text-white">
        <div className="container flex flex-col items-center justify-between gap-2 py-6 text-sm md:flex-row">
          <p className="opacity-70">
            © {currentYear} TIFPA Consulting LLC. All rights reserved.
          </p>
          <Link
            to={other.path}
            className="opacity-70 transition-opacity hover:opacity-100"
          >
            {other.title}
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;
